/**
 * Footer - Site footer with links, contact info, and social media
 */
import React, { memo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiPhone, FiMapPin, FiInstagram, FiFacebook, FiTwitter, FiLinkedin } from 'react-icons/fi';
import { FaYoutube, FaWhatsapp } from 'react-icons/fa';

import styles from './Footer.module.css';
import { ROUTES } from '@/config/routes';
import { APP_NAME, SUPPORT_EMAIL } from '@/config/constants';
import { getActiveCategories } from '@/services/firebase/categories.service';
import { CategoryData } from '@/data/categories';
import { useAppSelector } from '@/hooks';
import { selectFooterSettings, selectSocialLinks } from '@/store/slices/settingsSlice';
import { contactData } from '@/data/contact';

const Footer: React.FC = memo(() => {
  const currentYear = new Date().getFullYear();
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const footerSettings = useAppSelector(selectFooterSettings);
  const socialLinks = useAppSelector(selectSocialLinks);

  // Fetch categories from Firebase
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const fetchedCategories = await getActiveCategories();
        setCategories(fetchedCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
  }, []);

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        {/* Main Footer Content */}
        <div className={styles.mainContent}>
          {/* Brand Column */}
          <div className={styles.column}>
            <Link to={ROUTES.HOME} className={styles.logo}>
              <span className={styles.logoIcon}>{(footerSettings?.brand?.name || APP_NAME.toUpperCase()).charAt(0)}</span>
              <span className={styles.logoText}>
                <span className={styles.logoBrand}>{footerSettings?.brand?.name || APP_NAME.toUpperCase()}</span>
                <span className={styles.logoTagline}>{footerSettings?.brand?.tagline || 'DENTAL'}</span>
              </span>
            </Link>
            <p className={styles.description}>
              {footerSettings?.brand?.description || 'Your trusted partner for premium dental equipment and supplies. Quality products at competitive prices with dedicated support.'}
            </p>
            <div className={styles.social}>
              <a href={socialLinks?.facebook || 'https://facebook.com'} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <FiFacebook />
              </a>
              <a href={socialLinks?.instagram || 'https://instagram.com'} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <FiInstagram />
              </a>
              <a href={socialLinks?.twitter || 'https://twitter.com'} target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <FiTwitter />
              </a>
              <a href={socialLinks?.linkedin || 'https://linkedin.com'} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <FiLinkedin />
              </a>
              {socialLinks?.youtube && (
                <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                  <FaYoutube />
                </a>
              )}
              {socialLinks?.whatsapp && (
                <a href={`https://wa.me/${socialLinks.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                  <FaWhatsapp />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div className={styles.column}>
            <h4 className={styles.columnTitle}>Quick Links</h4>
            <ul className={styles.linkList}>
              {(footerSettings?.quickLinks || [
                { label: 'Home', path: ROUTES.HOME },
                { label: 'All Products', path: ROUTES.PRODUCTS },
                { label: 'About Us', path: ROUTES.ABOUT },
                { label: 'Contact Us', path: ROUTES.CONTACT },
              ]).map(link => (
                <li key={link.path}><Link to={link.path}>{link.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div className={styles.column}>
            <h4 className={styles.columnTitle}>Categories</h4>
            <ul className={styles.linkList}>
              {categories.slice(0, 6).map(category => (
                <li key={category.id}>
                  <Link to={`${ROUTES.PRODUCTS}?category=${category.slug}`}>
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Policies */}
          <div className={styles.column}>
            <h4 className={styles.columnTitle}>Policies</h4>
            <ul className={styles.linkList}>
              {(footerSettings?.policyLinks || [
                { label: 'Privacy Policy', path: ROUTES.PRIVACY },
                { label: 'Terms of Service', path: ROUTES.TERMS },
                { label: 'Shipping Policy', path: ROUTES.SHIPPING },
                { label: 'Return Policy', path: ROUTES.RETURNS },
                { label: 'FAQ', path: `${ROUTES.CONTACT}#faq` },
              ]).map(link => (
                <li key={link.path}><Link to={link.path}>{link.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className={styles.column}>
            <h4 className={styles.columnTitle}>Contact Us</h4>
            <ul className={styles.contactList}>
              <li>
                <FiMapPin />
                <span>{footerSettings?.contactInfo?.address || `${contactData.addresses[0].addressLine1}, ${contactData.addresses[0].city}, ${contactData.addresses[0].state} - ${contactData.addresses[0].pincode}, ${contactData.addresses[0].country}`}</span>
              </li>
              <li>
                <FiPhone />
                <a href={`tel:${(footerSettings?.contactInfo?.phone || '+91 98765 43210').replace(/\s/g, '')}`}>{footerSettings?.contactInfo?.phone || '+91 98765 43210'}</a>
              </li>
              <li>
                <FiMail />
                <a href={`mailto:${footerSettings?.contactInfo?.email || SUPPORT_EMAIL}`}>{footerSettings?.contactInfo?.email || SUPPORT_EMAIL}</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={styles.bottomBar}>
          <p className={styles.copyright}>
            {footerSettings?.copyrightText || `© ${currentYear} ${APP_NAME}. All rights reserved.`}
          </p>
          <div className={styles.payments}>
            <span>We accept:</span>
            <div className={styles.paymentIcons}>
              {(footerSettings?.paymentMethods || ['VISA', 'MC', 'UPI', 'COD']).map(method => (
                <span key={method} className={styles.paymentIcon}>{method}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = 'Footer';

export default Footer;
